import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { isInteractiveVideoId } from '../../shared/helpers/is-interactive-video-id';
import { LevelProgressService } from '../../shared/level-progress/level-progress.service';
import { ProfileServiceConfigurationService } from '../../shared/profile-service-configuration.service';
import { DEFAULT_CAROUSEL_SETTINGS } from '../../shared/types/CarouselSettings';
import { CourseContentType } from '../../shared/types/course-content-type';
import { GamificationActivityType } from '../../shared/types/gamification-activity-type';
import { StudyBundleCourseDto } from '../../shared/types/StudyBundleCourseDto';
import { CarouselTranslatedEvent } from '../immersion-carousel/carousel-translated-event';
import { ImmersionCarouselComponent } from '../immersion-carousel/immersion-carousel.component';
import { ImmersionCarouselService } from '../immersion-carousel/immersion-carousel.service';
import {
  INTERACTIVE_VIDEOS_MODAL_TITLE_ID,
  InteractiveVideosModalComponent,
} from '../../components/interactive-videos-modal/interactive-videos-modal.component';
import { PracticeSectionContent } from '../practice-section/practice-section.interface';
import { ImmersionSortBy } from '../../shared/types/immersion-sort-by';

@Component({
  selector: 'lp2-interactive-videos-carousel',
  templateUrl: './interactive-videos-carousel.component.html',
  styleUrls: ['./interactive-videos-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractiveVideosCarouselComponent extends ImmersionCarouselComponent implements PracticeSectionContent {
  @Input()
  set activityPoints(value: Map<GamificationActivityType, number>) {
    this.videosPoints = value ? value.get(GamificationActivityType.COMPLETE_PRACTICE_VIDEO) : 0;
  }

  // the fetched list stays whole: reloadCourseTileById derives a page from an item's index in it
  get immersionItems(): StudyBundleCourseDto[] {
    return this.fetchedItems;
  }

  set immersionItems(items: StudyBundleCourseDto[]) {
    this.fetchedItems = items;
    // the base class sets the items before this class's fields are initialised
    if (this.itemsSubject$) {
      this.itemsSubject$.next(items);
    }
  }

  videosPoints: number;
  readonly carouselSettings = DEFAULT_CAROUSEL_SETTINGS;
  readonly modalComponent = InteractiveVideosModalComponent;
  readonly modalTitleId = INTERACTIVE_VIDEOS_MODAL_TITLE_ID;

  readonly displayItems$: Observable<StudyBundleCourseDto[]>;
  readonly shouldDisplay$: Observable<boolean>;

  private fetchedItems: StudyBundleCourseDto[];
  private lastFetchWasFull = false;

  filteredStatuses: string[] = [];
  sortBy = ImmersionSortBy.SEQUENCE;

  private readonly itemsSubject$ = new BehaviorSubject<StudyBundleCourseDto[]>(this.fetchedItems);

  constructor(
    service: ImmersionCarouselService,
    levelProgressService: LevelProgressService,
    private readonly configurationService: ProfileServiceConfigurationService
  ) {
    super(service, levelProgressService, CourseContentType.VIDEOS);
    this.displayItems$ = this.itemsSubject$.pipe(
      map((items) => items.filter((item) => isInteractiveVideoId(item.mediaData)))
    );
    // no value until the fetch settles, so "still loading" is not treated as an empty carousel
    const settledFeed$ = combineLatest([
      this.configurationService.isInteractiveVideosCarouselEnabled$(),
      this.itemsLoaded$,
      this.displayItems$,
    ]).pipe(filter(([, loaded]) => loaded));
    this.shouldDisplay$ = settledFeed$.pipe(
      map(([enabled]) => enabled),
      distinctUntilChanged()
    );
  }

  getImmersionItems(): Observable<StudyBundleCourseDto[]> {
    return super
      .getImmersionItems()
      .pipe(
        tap((items: StudyBundleCourseDto[]) => (this.lastFetchWasFull = !!items && items.length === this.PAGE_SIZE))
      );
  }

  // owl's item count is the rendered count; ask whether the last fetch came back full instead
  onTranslated(event: CarouselTranslatedEvent): void {
    if (!this.lastFetchWasFull) {
      return;
    }
    super.onTranslated(event && { ...event, item: { ...event.item, count: this.PAGE_SIZE } });
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/interactive-videos-carousel/interactive-videos-carousel.component.ts